import type { CustomReportConfig } from './customReportTypes';

export const CUSTOM_REPORT_CONFIG_QUERY_KEY = 'custom_report_config_b64';
export const CUSTOM_REPORT_PRINT_QUERY_KEY = 'custom_report_print';

const safeDecodeURIComponent = (value: string) => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const fromBase64Utf8 = (value: string): string => {
    try {
        const binary = atob(value);

        return decodeURIComponent(
            Array.prototype.map
                .call(
                    binary,
                    (char: string) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`,
                )
                .join(''),
        );
    } catch {
        return '';
    }
};

const toBase64Utf8 = (value: string): string => {
    const utf8 = encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_, hex: string) =>
        String.fromCharCode(parseInt(hex, 16)),
    );

    return btoa(utf8);
};

export const encodeCustomReportConfig = (config: CustomReportConfig) =>
    toBase64Utf8(JSON.stringify(config));

export const decodeCustomReportConfig = (encoded: string): CustomReportConfig | null => {
    const candidates = [
        encoded,
        safeDecodeURIComponent(encoded),
        encoded.replace(/ /g, '+'),
        safeDecodeURIComponent(encoded).replace(/ /g, '+'),
    ];

    return candidates.reduce<CustomReportConfig | null>((resolved, candidate) => {
        if (resolved) return resolved;

        try {
            const parsed = JSON.parse(fromBase64Utf8(candidate)) as CustomReportConfig;
            if (parsed && Array.isArray(parsed.slides)) {
                return parsed;
            }
        } catch {
            return null;
        }
        return null;
    }, null);
};

export const getCustomReportConfigFromQuery = (): CustomReportConfig | null => {
    const params = new URLSearchParams(window.location.search);
    const rawValue = params.get(CUSTOM_REPORT_CONFIG_QUERY_KEY);

    if (!rawValue) return null;

    return decodeCustomReportConfig(rawValue);
};

export const shouldAutoPrintCustomReport = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get(CUSTOM_REPORT_PRINT_QUERY_KEY) === '1';
};
